import React from 'react'
import useCustomerConfig from '../hooks/useCustomerConfig'
import clsx from 'clsx'

export const Sidebar = ({
  extraChildren,
  isOpen,
  availableTabs,
  activeTab,
  setActiveTab,
  darkMode,
}: {
  extraChildren?: React.ReactNode
  isOpen: boolean
  availableTabs: Tab[]
  activeTab: string
  setActiveTab: (tab: string) => void
  darkMode: boolean
}) => {
  const customerConfig = useCustomerConfig()

  const logoUrl = customerConfig.headerLogoUrl
    ? darkMode
      ? customerConfig.headerLogoUrl.dark
      : customerConfig.headerLogoUrl.light
    : null
  const isSalubi = customerConfig.configName === 'salubi'

  return (
    <header
      className={clsx(
        customerConfig.theme.isLight
          ? 'bg-white text-black dark:bg-gray-900 dark:text-white'
          : `${customerConfig.theme.bgColor} text-white`,
        'h-full flex flex-col items-center justify-between',
        isOpen ? 'w-60' : 'w-0 p-0 overflow-hidden',
        'lg:w-60 transition-all duration-500 ease-in-out',
      )}
    >
      <div className="flex flex-col items-center w-full">
        <div
          className={`flex flex-col items-center rounded-xl text-center gap-4`}
        >
          {logoUrl ? (
            <div
              className={clsx(
                'mt-4 overflow-hidden shadow-md',
                isSalubi
                  ? 'w-28 h-28 rounded-xl bg-[#2f8f97] p-2'
                  : 'w-full max-h-[120px] p-4',
              )}
            >
              <img
                src={logoUrl}
                alt={customerConfig.header}
                className={clsx(
                  'w-full h-full',
                  isSalubi ? 'object-contain rounded-lg' : 'h-auto object-contain',
                )}
              />
            </div>
          ) : (
            <h1 className="text-2xl font-bold p-4 mt-2">
              {customerConfig.header}
            </h1>
          )}
          {customerConfig.subheader && (
            <span className={`text-center p-3`}>
              {customerConfig.subheader}
            </span>
          )}
        </div>

        {/* Rest of the component remains the same */}
        <Tabs
          activeTab={activeTab}
          availableTabs={availableTabs}
          setSelectedTab={setActiveTab}
          isSalubi={isSalubi}
        />
      </div>
      <div className="w-full">{extraChildren}</div>
    </header>
  )
}

export type Tab = {
  id: string
  label: string
  icon?: React.ReactNode
}

type TabProps = {
  activeTab: string
  availableTabs: Tab[]
  setSelectedTab: (tab: string) => void
  isSalubi?: boolean
}

const Tabs = (props: TabProps) => {
  return (
    <nav className="flex flex-col mt-10 w-full">
      {props.availableTabs.map((tab) => (
        <TabButton
          key={tab.id}
          active={props.activeTab === tab.id}
          onClick={() => props.setSelectedTab(tab.id)}
          isSalubi={props.isSalubi}
        >
          {tab.icon}
          {tab.label}
        </TabButton>
      ))}
    </nav>
  )
}

const TabButton = ({
  children,
  active,
  onClick,
  isSalubi,
}: {
  children: React.ReactNode
  active: boolean
  onClick: () => void
  isSalubi?: boolean
}) => {
  return (
    <button
      onClick={onClick}
      className={`p-5 flex gap-2 w-full border-l-4 dark:text-white ${
        active
          ? isSalubi
            ? 'border-teal-200 bg-white/15 dark:bg-white/10 dark:border-teal-100'
            : 'border-blue-500 bg-gray-100 dark:bg-gray-800 dark:border-blue-300'
          : 'border-transparent'
      }`}
    >
      {children}
    </button>
  )
}
